import React from 'react';
import { Alert, Button, Card, Typography } from 'antd';
import { ExclamationCircleOutlined, ReloadOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ComponentType<{ error: Error; retry: () => void }>;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    // You can log the error to an error reporting service here
    this.logErrorToService(error, errorInfo);
  }

  logErrorToService = (error: Error, errorInfo: React.ErrorInfo) => {
    // Log to console for development
    console.error('Error logged:', {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
    });

    // In production, you would send this to an error tracking service
    // Example: Sentry, LogRocket, etc.
  };

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        const FallbackComponent = this.props.fallback;
        return <FallbackComponent error={this.state.error!} retry={this.handleRetry} />;
      }

      return <DefaultErrorFallback error={this.state.error!} retry={this.handleRetry} />;
    }

    return this.props.children;
  }
}

interface ErrorFallbackProps {
  error: Error;
  retry: () => void;
}

export const DefaultErrorFallback: React.FC<ErrorFallbackProps> = ({ error, retry }) => {
  return (
    <Card style={{ margin: '20px', textAlign: 'center' }}>
      <ExclamationCircleOutlined style={{ fontSize: '48px', color: '#ff4d4f', marginBottom: '16px' }} />
      <Title level={3}>Something went wrong</Title>
      <Text type="secondary">
        An unexpected error occurred. Please try again or contact support if the problem persists.
      </Text>
      <div style={{ marginTop: '24px' }}>
        <Button type="primary" icon={<ReloadOutlined />} onClick={retry}>
          Try Again
        </Button>
      </div>
      {process.env.NODE_ENV === 'development' && (
        <Alert
          type="error"
          message="Development Error Details"
          description={error.message}
          style={{ marginTop: '16px', textAlign: 'left' }}
          showIcon
        />
      )}
    </Card>
  );
};

// Network Error Component
export const NetworkErrorFallback: React.FC<ErrorFallbackProps> = ({ retry }) => {
  return (
    <Card style={{ margin: '20px', textAlign: 'center' }}>
      <ExclamationCircleOutlined style={{ fontSize: '48px', color: '#faad14', marginBottom: '16px' }} />
      <Title level={3}>Network Error</Title>
      <Text type="secondary">
        Unable to connect to the server. Please check your internet connection and try again.
      </Text>
      <div style={{ marginTop: '24px' }}>
        <Button type="primary" icon={<ReloadOutlined />} onClick={retry}>
          Retry
        </Button>
      </div>
    </Card>
  );
};

// Unauthorized Error Component
export const UnauthorizedErrorFallback: React.FC = () => {
  const handleLogin = () => {
    window.location.href = '/login';
  };

  return (
    <Card style={{ margin: '20px', textAlign: 'center' }}>
      <ExclamationCircleOutlined style={{ fontSize: '48px', color: '#ff4d4f', marginBottom: '16px' }} />
      <Title level={3}>Access Denied</Title>
      <Text type="secondary">
        You don't have permission to access this resource. Please log in with appropriate credentials.
      </Text>
      <div style={{ marginTop: '24px' }}>
        <Button type="primary" onClick={handleLogin}>
          Go to Login
        </Button>
      </div>
    </Card>
  );
};

// Not Found Error Component
export const NotFoundErrorFallback: React.FC = () => {
  const handleGoBack = () => {
    window.history.back();
  };

  const handleGoHome = () => {
    window.location.href = '/';
  };

  return (
    <Card style={{ margin: '20px', textAlign: 'center' }}>
      <Title level={1} style={{ fontSize: '72px', margin: '0' }}>404</Title>
      <Title level={3}>Page Not Found</Title>
      <Text type="secondary">
        The page you are looking for doesn't exist or has been moved.
      </Text>
      <div style={{ marginTop: '24px' }}>
        <Button type="primary" onClick={handleGoHome} style={{ marginRight: '8px' }}>
          Go Home
        </Button>
        <Button onClick={handleGoBack}>
          Go Back
        </Button>
      </div>
    </Card>
  );
};

export default ErrorBoundary;
